import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Search, Palette, Camera, Share2, Heart,
  X, Download, Plus
} from 'lucide-react';

// i18n
import './i18n';

// Components
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';

// Pages
import HomePage from './pages/HomePage';
import GalleryPage from './pages/GalleryPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ExhibitionsPage from './pages/ExhibitionsPage';
import ArtworkDetailPage from './pages/ArtworkDetailPage';
import VirtualTourPage from './pages/VirtualTourPage';
import AdminDashboard from './pages/admin/AdminDashboard';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { GalleryProvider } from './context/GalleryContext';
import { ThemeProvider } from './context/ThemeContext';
import { AnalyticsProvider } from './context/AnalyticsContext';

// Services
import DatabaseService from './services/database';
import FileStorageService from './services/fileStorage';
import AnalyticsService from './services/analytics';


// Styles
import './styles/globals.css';
import './styles/animations.css';

/**
 * Main App Component - Modern Gallery with Advanced Features
 */
const App = () => {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Initialize services on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setLoadingProgress(20);
        
        // Initialize database
        await DatabaseService.initialize();
        setLoadingProgress(40);
        
        // Initialize file storage
        await FileStorageService.initialize();
        setLoadingProgress(60);
        
        // Initialize analytics
        await AnalyticsService.initialize();
        setLoadingProgress(80);
        
        // Pre-load critical assets
        await preloadCriticalAssets();
        setLoadingProgress(100);
        
        setIsInitialized(true);
      } catch (error) {
        console.error('App initialization failed:', error);
        setIsInitialized(true); // Continue anyway
      }
    };

    initializeApp();
  }, []);

  const preloadCriticalAssets = async () => {
    // Preload key images, fonts, and other assets
    const criticalAssets = [
      '/pics/IVP.png',
      '/pics/ivp_logo.png',
      // Add more critical assets
    ];

    const preloadPromises = criticalAssets.map(asset => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve; // Continue even if some assets fail
        img.src = asset;
      });
    });

    await Promise.all(preloadPromises);
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="w-32 h-32 mx-auto mb-6 relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-full h-full border-4 border-amber-500 border-t-transparent rounded-full"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Palette size={40} className="text-amber-500" />
              </div>
            </div>
          </motion.div>
          
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-serif text-white mb-4"
          >
{t('home.title')}
          </motion.h1>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-gray-300 mb-8"
          >
{t('home.loading')}
          </motion.p>
          
          <div className="w-64 mx-auto bg-gray-700 rounded-full h-2 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${loadingProgress}%` }}
              transition={{ duration: 0.3 }}
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
            />
          </div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-sm text-gray-400 mt-4"
          >
            {loadingProgress}%
          </motion.p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AnalyticsProvider>
          <AuthProvider>
            <GalleryProvider>
              <Router>
                <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
                  <Header />
                  
                  <Suspense fallback={<LoadingSpinner />}>
                    <main className="relative">
                      <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/gallery" element={<GalleryPage />} />
                        <Route path="/gallery/:category" element={<GalleryPage />} />
                        <Route path="/artwork/:id" element={<ArtworkDetailPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/exhibitions" element={<ExhibitionsPage />} />
                        <Route path="/virtual-tour" element={<VirtualTourPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        
                        {/* Admin Routes */}
                        <Route path="/admin/*" element={<AdminDashboard />} />
                        
                        {/* Fallback */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </main>
                  </Suspense>
                  
                  <Footer />
                  
                  {/* Global Components */}
                  <FloatingActionButton />
                  <CookieConsent />
                  <PWAInstallPrompt />
                  
                  {/* Toast Notifications */}
                  <Toaster 
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#1f2937',
                        color: '#f9fafb',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                      },
                    }}
                  />
                </div>
              </Router>
            </GalleryProvider>
          </AuthProvider>
        </AnalyticsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

// Floating Action Button Component
const FloatingActionButton = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="mb-4 space-y-3"
          >
            <FloatingButton icon={Camera} label="VR Тур" />
            <FloatingButton icon={Share2} label="Поделиться" />
            <FloatingButton icon={Heart} label="Избранное" />
            <FloatingButton icon={Search} label="Поиск" />
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl transition-shadow"
      >
        <motion.div
          animate={{ rotate: isExpanded ? 45 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <Plus size={24} />
        </motion.div>
      </motion.button>
    </div>
  );
};

const FloatingButton = ({ icon: Icon, label }) => (
  <motion.button
    whileHover={{ scale: 1.05, x: -10 }}
    whileTap={{ scale: 0.95 }}
    className="flex items-center space-x-3 bg-white shadow-lg rounded-full px-4 py-3 text-gray-700 hover:text-amber-600 transition-colors"
  >
    <Icon size={20} />
    <span className="text-sm font-medium whitespace-nowrap">{label}</span>
  </motion.button>
);

// Cookie Consent Component
const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setTimeout(() => setShowConsent(true), 2000);
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setShowConsent(false);
  };
  
  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'declined');
    setShowConsent(false);
  };
  
  if (!showConsent) return null;
  
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-6 z-50"
    >
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
        <div className="flex-1">
          <p className="text-sm">
            Мы используем файлы cookie для улучшения работы сайта и аналитики посещений. 
            Продолжая использовать сайт, вы соглашаетесь с нашей{' '}
            <a href="/privacy" className="text-amber-400 hover:underline">
              политикой конфиденциальности
            </a>.
          </p>
        </div>
        <div className="flex space-x-4">
          <button
            onClick={handleDecline}
            className="px-4 py-2 border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Отклонить
          </button>
          <button
            onClick={handleAccept}
            className="px-4 py-2 bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
          >
            Принять
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// PWA Install Prompt Component
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };
  
  if (!showPrompt) return null;
  
  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      className="fixed top-24 right-6 bg-white shadow-xl rounded-lg p-4 max-w-sm z-50 border border-gray-200"
    >
      <div className="flex items-start space-x-3">
        <div className="p-2 bg-amber-100 rounded-lg">
          <Download size={20} className="text-amber-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">
            Установить приложение
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Добавьте галерею на главный экран для быстрого доступа
          </p>
          <div className="flex space-x-2">
            <button
              onClick={handleInstall}
              className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 transition-colors"
            >
              Установить
            </button>
            <button
              onClick={() => setShowPrompt(false)}
              className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors"
            >
              Позже
            </button>
          </div>
        </div>
        <button
          onClick={() => setShowPrompt(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <X size={16} />
        </button>
      </div>
    </motion.div>
  );
};

export default App;